import { Controller } from '@hotwired/stimulus'

const MAX_BUTTONS = 5

// Manages the button repeater inside the create-template modal.
// Server (WhatsappBusiness::TemplateButtons) is the source of truth for validation;
// this controller only handles repeater UX (add/remove/reindex/mode toggle).
export default class extends Controller {
  static targets = ['list', 'addButton']

  add(event) {
    event.preventDefault()
    if (this.rowCount() >= MAX_BUTTONS) return

    const index = this.rowCount()
    const row = this.buildRow(index)
    this.listTarget.appendChild(row)
    this.refreshAddButton()
    row.querySelector('input[name$="[text]"]').focus()
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest('[data-template-buttons-row]')
    if (!row) return
    row.remove()
    this.reindex()
    this.refreshAddButton()
  }

  rowCount() {
    return this.listTarget.querySelectorAll('[data-template-buttons-row]').length
  }

  // Replaces the whole repeater with quick-reply buttons; used by the utility
  // review rail when the operator picks an AI rewrite (its body references
  // these buttons, so they come along).
  setQuickReplies(texts) {
    this.listTarget.innerHTML = ''
    texts.slice(0, MAX_BUTTONS).forEach((text, index) => {
      const row = this.buildRow(index)
      this.listTarget.appendChild(row)
      row.querySelector('input[name$="[text]"]').value = text.slice(0, 25)
      const quickReply = row.querySelector('input[type="radio"][value="quick_reply"]')
      quickReply.checked = true
      quickReply.dispatchEvent(new Event('change', { bubbles: true }))
    })
    this.refreshAddButton()
  }

  refreshAddButton() {
    const atCapacity = this.rowCount() >= MAX_BUTTONS
    this.addButtonTarget.classList.toggle('hidden', atCapacity)
  }

  reindex() {
    this.listTarget.querySelectorAll('[data-template-buttons-row]').forEach((row, index) => {
      row.querySelector('[data-row-label]').textContent = `Botão ${index + 1}`
      row.querySelectorAll('input[name*="whatsapp_template[buttons]"]').forEach((input) => {
        input.name = input.name.replace(/whatsapp_template\[buttons\]\[\d+\]/, `whatsapp_template[buttons][${index}]`)
      })
      row.querySelectorAll('input[type="radio"][name^="button_mode_"]').forEach((input) => {
        input.name = `button_mode_${index}`
      })
    })
  }

  toggleMode(event) {
    const row = event.currentTarget.closest('[data-template-buttons-row]')
    if (!row) return

    const mode = event.currentTarget.value
    const wrapper = row.querySelector('[data-url-wrapper]')
    const urlInput = wrapper.querySelector('input')
    const typeInput = row.querySelector('input[name$="[type]"]')

    if (mode === 'quick_reply') {
      typeInput.value = 'QUICK_REPLY'
      wrapper.classList.add('hidden')
      urlInput.required = false
      urlInput.value = ''
    } else if (mode === 'fixed') {
      typeInput.value = 'URL'
      wrapper.classList.remove('hidden')
      urlInput.required = true
    } else {
      typeInput.value = 'URL'
      wrapper.classList.add('hidden')
      urlInput.required = false
      urlInput.value = ''
    }
  }

  buildRow(index) {
    const row = document.createElement('div')
    row.className = 'rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3'
    row.dataset.templateButtonsRow = ''

    row.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold uppercase tracking-wide text-gray-500" data-row-label>Botão ${index + 1}</span>
        <button type="button" class="text-xs text-gray-500 hover:text-red-600 transition-colors" data-action="click->template-buttons#remove">Remover</button>
      </div>

      <input type="hidden" name="whatsapp_template[buttons][${index}][type]" value="URL" />

      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Texto do botão</label>
        <input
          type="text"
          name="whatsapp_template[buttons][${index}][text]"
          maxlength="25"
          required
          placeholder="Ver imóvel"
          class="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none"
        />
        <p class="text-xs text-gray-400 mt-0.5">Até 25 caracteres</p>
      </div>

      <div>
        <span class="block text-xs font-medium text-gray-600 mb-1">Tipo do botão</span>
        <div class="grid grid-cols-3 gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5 text-sm">
          <label class="cursor-pointer">
            <input
              type="radio"
              name="button_mode_${index}"
              value="dispatch"
              class="peer sr-only"
              data-action="change->template-buttons#toggleMode"
              checked
            />
            <span class="block text-center px-2 py-1.5 rounded-md text-gray-600 peer-checked:bg-gray-900 peer-checked:text-white peer-checked:shadow-sm transition-colors">
              Link no disparo
            </span>
          </label>
          <label class="cursor-pointer">
            <input
              type="radio"
              name="button_mode_${index}"
              value="fixed"
              class="peer sr-only"
              data-action="change->template-buttons#toggleMode"
            />
            <span class="block text-center px-2 py-1.5 rounded-md text-gray-600 peer-checked:bg-gray-900 peer-checked:text-white peer-checked:shadow-sm transition-colors">
              URL fixa
            </span>
          </label>
          <label class="cursor-pointer">
            <input
              type="radio"
              name="button_mode_${index}"
              value="quick_reply"
              class="peer sr-only"
              data-action="change->template-buttons#toggleMode"
            />
            <span class="block text-center px-2 py-1.5 rounded-md text-gray-600 peer-checked:bg-gray-900 peer-checked:text-white peer-checked:shadow-sm transition-colors">
              Resposta rápida
            </span>
          </label>
        </div>

        <div class="hidden mt-2" data-url-wrapper>
          <input
            type="text"
            inputmode="url"
            name="whatsapp_template[buttons][${index}][url]"
            placeholder="https://imobiliaria.com.br"
            class="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none font-mono"
          />
          <p class="text-xs text-gray-400 mt-0.5">Deve começar com https://. Termine com {{1}} para preencher parte do link na hora do envio (ex: https://imobiliaria.com.br/imovel/{{1}})</p>
        </div>
      </div>
    `

    return row
  }
}
